import { motion } from 'framer-motion';
import Certifications from '../components/Certifications';

const skillClusters = [
  {
    id: 'data',
    title: 'Data & Analytics',
    description: 'End-to-end analytical work from raw data to decision-ready output. Regression, classification, time-series, and statistical inference.',
    skills: ['Python', 'SQL', 'R', 'Pandas', 'NumPy', 'Power BI', 'DAX', 'Power Query', 'Streamlit', 'Statistical Modelling', 'Hypothesis Testing', 'Econometrics'],
    accent: '#00E5FF',
    featured: true,
  },
  {
    id: 'ml',
    title: 'Machine Learning',
    description: 'Supervised learning, model evaluation, and explainability. Production-oriented, not demonstration-oriented.',
    skills: ['Scikit-learn', 'XGBoost', 'SHAP', 'Feature Engineering', 'Churn Modelling', 'Temporal ML', 'Model Evaluation', 'RLHF', 'LLM Evaluation'],
    accent: '#9B3FFE',
    featured: false,
  },
  {
    id: 'strategy',
    title: 'Business & Strategy',
    description: 'Structured problem framing and commercial analysis. Frameworks applied to real decisions, not academic exercises.',
    skills: ['Business Analysis', 'Strategic Analysis', 'PESTEL', "Porter's Five Forces", 'RBV', 'Stakeholder Management', 'Policy Analysis', 'Expert Consulting'],
    accent: '#10B981',
    featured: false,
  },
  {
    id: 'tools',
    title: 'Tools & Infrastructure',
    description: 'The technical layer that connects analysis to output: automation, integration, and deployment.',
    skills: ['n8n', 'Make (Integromat)', 'API Integration', 'ChatGPT API', 'Gemini API', 'SPSS', 'Git', 'Prompt Engineering'],
    accent: '#F59E0B',
    featured: false,
  },
];

const LANGUAGES = [
  { name: 'English', level: 'Native', accent: '#9B3FFE' },
  { name: 'Gujarati', level: 'Native', accent: '#9B3FFE' },
  { name: 'Hindi', level: 'Full Professional', accent: '#00E5FF' },
  { name: 'Urdu', level: 'Full Professional', accent: '#00E5FF' },
  { name: 'Arabic', level: 'Professional Working', accent: '#10B981' },
  { name: 'French', level: 'Working', accent: '#F59E0B' },
];

const SkillCard = ({ cluster, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-40px' }}
    transition={{ duration: 0.6, delay: index * 0.07, ease: [0.25, 0.1, 0.25, 1] }}
    className={`group p-6 md:p-7 rounded-2xl border transition-all duration-300 glow-border ${
      cluster.featured ? 'md:col-span-2' : ''
    }`}
    style={{
      borderColor: 'rgba(255,255,255,0.07)',
      background: cluster.featured
        ? `linear-gradient(135deg, ${cluster.accent}08 0%, rgba(10,10,10,0.85) 60%)`
        : 'rgba(10,10,10,0.65)',
    }}
    data-testid={`skill-category-${index}`}
  >
    {/* Accent dot + title */}
    <div className="flex items-start justify-between mb-3">
      <div className="flex items-center gap-3">
        <div
          className="w-2 h-2 rounded-full flex-shrink-0 mt-0.5"
          style={{ backgroundColor: cluster.accent }}
        />
        <h3 className="font-display text-lg md:text-xl font-medium tracking-tight">
          {cluster.title}
        </h3>
      </div>
      {cluster.featured && (
        <span
          className="text-[9px] uppercase tracking-[0.28em] font-medium mt-0.5"
          style={{ color: cluster.accent }}
        >
          Primary
        </span>
      )}
    </div>

    <p className="text-text-secondary text-sm mb-5 pl-5 leading-relaxed">
      {cluster.description}
    </p>

    <div className="flex flex-wrap gap-2 pl-5">
      {cluster.skills.map((skill, i) => (
        <span
          key={i}
          className="px-3 py-1.5 text-[11.5px] rounded-full text-text-secondary border transition-colors duration-200 hover:text-white/80"
          style={{
            background: 'rgba(255,255,255,0.035)',
            borderColor: 'rgba(255,255,255,0.07)',
          }}
        >
          {skill}
        </span>
      ))}
    </div>
  </motion.div>
);

const Skills = () => {
  return (
    <section id="skills" className="relative py-20 sm:py-28 md:py-40" data-testid="skills-section">
      <div className="absolute top-0 left-0 right-0 h-px section-divider" />

      {/* Subtle background differentiation */}
      <div
        className="absolute inset-0"
        style={{ background: 'radial-gradient(ellipse at 80% 20%, rgba(0,229,255,0.03) 0%, transparent 60%)' }}
      />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.75, ease: [0.25, 0.1, 0.25, 1] }}
          className="mb-14 md:mb-16"
        >
          <div className="section-label mb-6">Capabilities</div>
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-none font-medium">
              Skills &<br />Expertise
            </h2>
            <p className="text-text-secondary text-sm max-w-[260px] leading-relaxed pb-1 sm:text-right">
              Four capability clusters covering the full analytical stack: data, machine learning, strategy, and tooling.
            </p>
          </div>
        </motion.div>

        {/* Skill clusters — bento grid */}
        <div className="grid md:grid-cols-2 gap-4 md:gap-5 mb-20">
          {skillClusters.map((cluster, index) => (
            <SkillCard key={cluster.id} cluster={cluster} index={index} />
          ))}
        </div>

        <Certifications />

        {/* Education */}
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.75, ease: [0.25, 0.1, 0.25, 1] }}
          className="grid md:grid-cols-2 gap-5"
        >
          <div
            className="p-8 rounded-2xl border"
            style={{
              background: 'linear-gradient(135deg, rgba(155, 63, 254, 0.09) 0%, rgba(10,10,10,0.8) 70%)',
              borderColor: 'rgba(155, 63, 254, 0.20)',
            }}
          >
            <span className="section-label mb-4 block">Current</span>
            <h3 className="font-display text-2xl md:text-3xl font-medium mb-2 tracking-tight">
              MSc Data Science
            </h3>
            <p className="text-text-secondary text-sm mb-1">Loughborough University</p>
            <p className="text-text-secondary text-sm">2025–2026 · Distinction Expected</p>
          </div>

          <div
            className="p-8 rounded-2xl border"
            style={{ background: 'rgba(10,10,10,0.65)', borderColor: 'rgba(255,255,255,0.07)' }}
          >
            <span className="section-label mb-4 block">Foundation</span>
            <h3 className="font-display text-2xl md:text-3xl font-medium mb-2 tracking-tight">
              BSc Economics (2:1)
            </h3>
            <p className="text-text-secondary text-sm mb-1">University of Westminster</p>
            <p className="text-text-secondary text-sm">2022–2025</p>
          </div>
        </motion.div>

        {/* Languages */}
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.75, delay: 0.1, ease: [0.25, 0.1, 0.25, 1] }}
          className="mt-12"
        >
          <p className="section-label mb-6">Languages</p>
          <div className="flex flex-wrap gap-3">
            {LANGUAGES.map((lang, i) => (
              <div
                key={i}
                className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl border"
                style={{ borderColor: 'rgba(255,255,255,0.08)', background: 'rgba(10,10,10,0.65)' }}
              >
                <div
                  className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: lang.accent }}
                />
                <span className="text-sm text-white/80 font-medium">{lang.name}</span>
                <span className="text-[10.5px] text-text-secondary">{lang.level}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Skills;
